import { computed, ref } from 'vue'
import { getUniversityList } from '../Suggest/useUniversityList'
import { getDepartmentList } from '../Suggest/useDepartmentList'
import {
  isHumanitiesOrScienceNum,
  isSchoolDepartmentNum,
  isGraduationYearNum,
  isGraduationMonthNum,
} from '@/studentsite/signup/signup/guard'
import { countVisibleLength } from '@/utils/countVisibleLength'

/**
 * @import { Ref } from 'vue'
 * @import { University } from '../Suggest/useUniversityList'
 * @import { Department } from '../Suggest/useDepartmentList'
 * @import DepartmentSelection from '../PartsDepartmentSelection/DepartmentSelection.vue'
 * @import UniversitySelection from '../PartsUniversitySelection/UniversitySelection.vue'
 * @import { Props, Emits } from './emitTypes'
 */

/**
 *
 * @param {Ref<DepartmentSelection|undefined>} refDepartmentSelection
 * @param {Ref<UniversitySelection|undefined>} refUniversitySelection
 * @param {Emits} emits
 * @returns
 */
export const setup = (refDepartmentSelection, refUniversitySelection, emits) => {
  const oldUniversityName =
    /** @type {HTMLInputElement|null} */ (document.getElementById('old_university_name'))?.value ?? ''
  const oldUniversityCd =
    /** @type {HTMLInputElement|null} */ (document.getElementById('old_university_cd'))?.value ?? ''
  const oldAbroad = /** @type {HTMLInputElement|null} */ (document.getElementById('old_abroad'))?.value ?? '0'
  const oldFaculty = /** @type {HTMLInputElement|null} */ (document.getElementById('old_faculty'))?.value ?? ''
  const oldFacultyCd = /** @type {HTMLInputElement|null} */ (document.getElementById('old_faculty_cd'))?.value ?? ''
  const oldHumanitiesOrScience = Number(
    /** @type {HTMLInputElement|null} */ (document.getElementById('old_humanities_or_science'))?.value ?? '0',
  )
  const oldSchoolDepartment = Number(
    /** @type {HTMLInputElement|null} */ (document.getElementById('old_school_department'))?.value ?? '0',
  )
  const oldGraduationYear = Number(
    /** @type {HTMLInputElement|null} */ (document.getElementById('old_graduation_year'))?.value ?? '0',
  )
  const oldGraduationMonth = Number(
    /** @type {HTMLInputElement|null} */ (document.getElementById('old_graduation_month'))?.value ?? '3',
  )

  /** @type {Ref<string>} */
  const universityName = ref(oldUniversityName)
  /** @type {Ref<string>} */
  const universityCd = ref(oldUniversityCd)
  /** @type {Ref<boolean>} */
  const isAbroad = ref(oldAbroad == '1')
  /** @type {Ref<string>} */
  const faculty = ref(oldFaculty)
  /** @type {Ref<string>} */
  const facultyCd = ref(oldFacultyCd)
  /** @type {Ref<number>} */
  const humanitiesOrScience = ref(isHumanitiesOrScienceNum(oldHumanitiesOrScience) ? oldHumanitiesOrScience : 0)
  /** @type {Ref<number>} */
  const schoolDepartment = ref(isSchoolDepartmentNum(oldSchoolDepartment) ? oldSchoolDepartment : 0)
  /** @type {Ref<number>} */
  const graduationYear = ref(isGraduationYearNum(oldGraduationYear) ? oldGraduationYear : 0)
  /** @type {Ref<number>} */
  const graduationMonth = ref(isGraduationMonthNum(oldGraduationMonth) ? oldGraduationMonth : 0)
  /** @type {Ref<boolean>} */
  const acceptTerm = ref(false)

  /** */
  const submittable = computed(() => {
    return (
      acceptTerm.value &&
      (universityCd.value.length || (isAbroad.value && universityName.value.length)) &&
      isValidUniversityName.value &&
      (facultyCd.value.length || (isAbroad.value && faculty.value.length)) &&
      isValidUniversityDepartmentName.value &&
      humanitiesOrScience.value &&
      schoolDepartment.value &&
      graduationYear.value &&
      graduationMonth.value &&
      isValidGraduationConsistency.value
    )
  })

  /** @type Ref<Array<University>|[]> */
  const universityList = ref([])
  /** @type Ref<Array<Department>|[]> */
  const departmentList = ref([])

  /** @type {Ref<Array<number>>} */
  const graduationYearList = ref(_getGraduationYearList())
  /** @type {Ref<Array<number>>} */
  const graduationMonthList = ref(_getGraduationMonthList())

  /** @type { Ref<boolean> } */
  const isValidUniversityName = ref(true)
  /** @type { Ref<string[]> } */
  const listUniversityNameValidation = ref([])
  /** @type { Ref<boolean> } */
  const isValidUniversityDepartmentName = ref(true)
  /** @type { Ref<string[]> } */
  const listUniversityDepartmentNameValidation = ref([])

  /** @type { Ref<boolean> } */
  const isValidGraduationConsistency = ref(true)
  /** @type { Ref<string[]> } */
  const listGraduationConsistencyValidation = ref([])

  /**
   * @param {string} val
   */
  const checkUniversityName = val => {
    const length = countVisibleLength(val)
    if (length > 100) {
      listUniversityNameValidation.value = ['学校名は100文字以内で入力してください']
      isValidUniversityName.value = false
    } else if (!isAbroad.value && val.length && !universityCd.value.length) {
      listUniversityNameValidation.value = ['学校名はサジェストから選択してください']
      isValidUniversityName.value = false
    } else {
      isValidUniversityName.value = true
      listUniversityNameValidation.value = []
    }
  }

  /**
   * @param {string} val
   */
  const checkUniversityDepartmentName = val => {
    const length = countVisibleLength(val)
    if (length > 100) {
      listUniversityDepartmentNameValidation.value = ['学部/研究科名は100文字以内で入力してください']
      isValidUniversityDepartmentName.value = false
    } else if (!isAbroad.value && val.length && !facultyCd.value.length) {
      listUniversityDepartmentNameValidation.value = ['学部/研究科名はサジェストから選択してください']
      isValidUniversityDepartmentName.value = false
    } else {
      isValidUniversityDepartmentName.value = true
      listUniversityDepartmentNameValidation.value = []
    }
  }

  const checkGraduateYearMonth = () => {
    const selectedYear = graduationYear.value
    const selectedMonth = graduationMonth.value

    const currentMonth = new Date().getMonth() + 1
    const startYear = currentMonth < 4 ? new Date().getFullYear() - 1 : new Date().getFullYear()
    // 入力時点の月が1～3月かつ前年の1～3月卒を選択
    if (
      currentMonth <= 3 && //入力時点の月が1~3月
      selectedYear == startYear && //かつ前年指定
      selectedMonth >= 1 &&
      selectedMonth <= 3 // かつ1~3月指定
    ) {
      isValidGraduationConsistency.value = false
      listGraduationConsistencyValidation.value = ['卒業年月が過去の年月で登録されています']
    }
    // 入力時の月が4-12月である、かつ、入力フォームで当年(今年) の 1～3月を選択
    else if (4 <= currentMonth && selectedMonth >= 1 && selectedMonth <= 3 && selectedYear == startYear) {
      isValidGraduationConsistency.value = false
      listGraduationConsistencyValidation.value = ['卒業年月が過去の年月で登録されています']
    } else if (selectedYear <= startYear && selectedMonth < currentMonth) {
      isValidGraduationConsistency.value = false
      listGraduationConsistencyValidation.value = ['卒業年月が過去の年月で登録されています']
    } else {
      isValidGraduationConsistency.value = true
      listGraduationConsistencyValidation.value = []
    }
  }

  /**
   * @param {string} value
   */
  const onChangeUniversityName = async value => {
    universityCd.value = ''
    if (!isAbroad.value) {
      facultyCd.value = ''
      faculty.value = ''
      refDepartmentSelection.value?.clearValue()
    }

    // サジェスト
    if (value.length >= 1 && !isAbroad.value) {
      universityList.value = await getUniversityList(value)
    } else {
      universityList.value = []
    }

    //
    if (value.length == 0) {
      universityName.value = ''
    } else {
      universityName.value = value
    }
  }

  /**
   * @param {University} selection
   */
  const onChangeUniversitySelection = selection => {
    universityCd.value = selection.cd
    universityName.value = selection.text
    facultyCd.value = ''
    faculty.value = ''
    refDepartmentSelection.value?.clearValue()

    checkUniversityName(universityName.value)
    checkUniversityDepartmentName(faculty.value)
  }

  /**
   * @param {string} value
   */
  const onChangeUniversityDepartmentName = async value => {
    facultyCd.value = ''

    // サジェスト
    if (universityCd.value && !isAbroad.value) {
      departmentList.value = await getDepartmentList(value, universityCd.value)
    } else {
      departmentList.value = []
    }

    //
    if (value.length == 0) {
      faculty.value = ''
      refDepartmentSelection.value?.clearValue()
    } else {
      faculty.value = value
    }
  }

  /**
   * @param {Department} selection
   */
  const onChangeUniversityDepartmentSelection = selection => {
    facultyCd.value = selection.cd
    faculty.value = selection.text

    checkUniversityName(universityName.value)
    checkUniversityDepartmentName(faculty.value)
  }

  /**
   */
  const onChangeAbroad = () => {
    isAbroad.value = !isAbroad.value
    if (!isAbroad.value) {
      universityName.value = ''
      faculty.value = ''
      refUniversitySelection.value?.clearValue()
      refDepartmentSelection.value?.clearValue()
    } else {
      universityCd.value = ''
      facultyCd.value = ''
    }
    checkUniversityName(universityName.value)
    checkUniversityDepartmentName(faculty.value)

    universityList.value = []
    departmentList.value = []
  }

  const submit = () => {
    emits('submit')
  }

  return {
    universityName,
    universityCd,
    isAbroad,
    faculty,
    facultyCd,
    humanitiesOrScience,
    schoolDepartment,
    graduationYear,
    graduationMonth,
    acceptTerm,
    submittable,

    universityList,
    departmentList,
    graduationYearList,
    graduationMonthList,
    onChangeUniversityName,
    onChangeUniversitySelection,
    onChangeUniversityDepartmentName,
    onChangeUniversityDepartmentSelection,
    onChangeAbroad,
    submit,

    listUniversityNameValidation,
    listUniversityDepartmentNameValidation,
    checkUniversityName,
    checkUniversityDepartmentName,
    isValidGraduationConsistency,
    listGraduationConsistencyValidation,
    checkGraduateYearMonth,
  }
}

/**
 * @returns {Array<number>}
 */
const _getGraduationYearList = () => {
  const currentMonth = new Date().getMonth() + 1
  const currentYear = currentMonth < 4 ? new Date().getFullYear() - 1 : new Date().getFullYear()
  return Array(8)
    .fill(0)
    .map((_, i) => i + currentYear)
}

/**
 * @returns {Array<number>}
 */
const _getGraduationMonthList = () => {
  return Array(12)
    .fill(0)
    .map((_, i) => i + 1)
}
